import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Lottie from 'lottie-react';
import api from 'app/utils/axiosInstance';
import { parseApiError } from 'app/utils/errorHandler';
import { userLoggedIn } from 'app/redux/auth/authSlice';
import colors, { useColors } from 'app/utils/colors';
import { motion } from 'framer-motion';

const DEFAULT_STEPS = [
  { title: "Open Google Authenticator", desc: "Launch the app on your phone." },
  { title: "Find your code", desc: "Locate the 6-digit code for this account." },
  { title: "Enter it here", desc: "Type the code to confirm your identity." },
];

const cardVariants = {
  initial: { opacity: 0, x: 60, scale: 0.97 },
  animate: { opacity: 1, x: 0, scale: 1 },
  exit: { opacity: 0, x: -60, scale: 0.97 },
};
const cardTransition = { type: "tween", ease: "easeInOut", duration: 0.28 };

export default function TwoFactorVerify() {
  const dynamicColors = useColors();
  const primaryColor = dynamicColors.primary || 'var(--theme-primary, #f95f1f)';
  const [mode, setMode] = useState('totp'); // 'totp' | 'recovery'
  const [recoveryCode, setRecoveryCode] = useState('');
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation('auth');

  const STEPS = t('twoFactor.steps', { returnObjects: true }) || DEFAULT_STEPS;

  const getEffectiveUserId = () => {
    const fromUrl = searchParams.get('userId');
    if (fromUrl) {
      sessionStorage.setItem('pending_2fa_userId', fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem('pending_2fa_userId');
  };

  useEffect(() => {
    const userId = getEffectiveUserId();
    if (!userId) {
      toast.error('Session expired. Please sign in again.');
      navigate('/sessions/signin');
      return;
    }

    if (mode === 'totp') {
      inputRefs.current[0]?.focus();
    }
  }, [mode, navigate, searchParams]);

  const handleBackClick = () => {
    if (mode === 'recovery') {
      setMode('totp');
      setError('');
    } else {
      sessionStorage.removeItem('pending_2fa_userId');
      sessionStorage.removeItem('pending_external_token');
      navigate('/sessions/signin');
    }
  };

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    setError('');
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0)
      inputRefs.current[index - 1]?.focus();
    if (e.key === 'Enter') handleVerify();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...digits];
    pasted.split('').forEach((ch, i) => { next[i] = ch; });
    setDigits(next);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerify = async () => {
    const token = digits.join('');
    if (token.length !== 6) {
      setError(t('twoFactor.errors.invalidLength') || 'Enter the 6-digit code from Google Authenticator.');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const userId = getEffectiveUserId();
      if (!userId) {
        const sessionMsg = 'Session expired. Please sign in again.';
        setError(sessionMsg);
        toast.error(sessionMsg);
        navigate('/sessions/signin');
        return;
      }
      const externalToken = sessionStorage.getItem('pending_external_token'); // NEW
  
      const { data } = await api.post('/auth/login-2fa', { userId, token });
  
      const accessToken = data?.access_token || data?.accessToken || data?.token;
      const user = data?.user || null;
  
      sessionStorage.removeItem('pending_2fa_userId');
      sessionStorage.removeItem('pending_external_token'); // NEW — clean up
  
      if (accessToken) {
        const authData = { accessToken, user, externalToken }; // NEW — included here
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('auth_user', JSON.stringify(authData));
        dispatch(userLoggedIn(authData));
      }
      setSuccess(true);
      setTimeout(() => navigate('/'), 2200);
    } catch (err) {
      const displayMsg = parseApiError(
        err,
        t('twoFactor.errors.invalidCode') || 'Invalid code. Check Google Authenticator and try again.'
      );
      setError(displayMsg);
      toast.error(displayMsg);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setDigits(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyRecovery = async () => {
    if (!recoveryCode.trim()) {
      setError(t('twoFactor.recovery.errors.required') || 'Please enter a recovery code.');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const userId = getEffectiveUserId();
      if (!userId) {
        const sessionMsg = 'Session expired. Please sign in again.';
        setError(sessionMsg);
        toast.error(sessionMsg);
        navigate('/sessions/signin');
        return;
      }
      const externalToken = sessionStorage.getItem('pending_external_token');

      const { data } = await api.post('/auth/login-recovery', {
        userId,
        recoveryCode: recoveryCode.trim(),
      });

      const accessToken = data?.access_token || data?.accessToken || data?.token;
      const user = data?.user || null;

      sessionStorage.removeItem('pending_2fa_userId');
      sessionStorage.removeItem('pending_external_token');
      sessionStorage.setItem('signedInViaRecovery', 'true');

      if (accessToken) {
        const authData = { accessToken, user, externalToken };
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('auth_user', JSON.stringify(authData));
        dispatch(userLoggedIn(authData));
      }
      setSuccess(true);
      setTimeout(() => navigate('/'), 2200);
    } catch (err) {
      const displayMsg = parseApiError(
        err,
        t('twoFactor.recovery.errors.invalid') || 'Invalid recovery code.'
      );
      setError(displayMsg);
      toast.error(displayMsg);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  };

  const filled = digits.filter(Boolean).length;

  return (
    <motion.div
      variants={cardVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={cardTransition}
      style={{ width: "100%", maxWidth: 800 }}
    >
      <div className="auth-card">
        {/* LEFT — dark info panel */}
        <div className="auth-left">
          <div>
            <div className="auth-logo-row">
              <img src="/assets/icons/logo-client.svg" alt="Billswift" className="auth-logo" />
            </div>

            <div className="auth-eyebrow">
              {mode === 'recovery' ? t('twoFactor.recovery.eyebrow') || 'Account recovery' : t('twoFactor.eyebrow')}
            </div>
            <h1 className="auth-headline">
              {mode === 'recovery' ? (
                <>
                  {t('twoFactor.recovery.headline.line1') || 'Recover your'}
                  <br />
                  <span className="accent-gradient-text">
                    {t('twoFactor.recovery.headline.accent') || 'account.'}
                  </span>
                </>
              ) : (
                <>
                  {t('twoFactor.headline.line1')}
                  <br />
                  <span className="accent-gradient-text">{t('twoFactor.headline.accent')}</span>
                </>
              )}
            </h1>
            <p className="auth-sub-copy">
              {mode === 'recovery'
                ? t('twoFactor.recovery.subCopy') || 'Enter one of your 12-character one-time recovery codes to sign in.'
                : t('twoFactor.subCopy')}
            </p>

            <div className="steps-list">
              {STEPS.map((s, i) => (
                <div className="step-item" key={s.title}>
                  <div className="step-marker">
                    <span className="step-number">{i + 1}</span>
                    {i < STEPS.length - 1 && <span className="step-connector" />}
                  </div>
                  <div className="step-copy">
                    <div className="step-title">{s.title}</div>
                    <div className="step-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="auth-left-footer">{t('signIn.footer')}</div>
        </div>

        {/* RIGHT — form panel */}
        <div className="auth-right">
          <div className="auth-right-body">
            {!success && (
              <button
                type="button"
                className="back-icon-btn"
                onClick={handleBackClick}
                aria-label={t("signUp.backButtonLabel") || "Back"}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {success ? (
              <div className="tfa-success">
                <div className="tfa-lottie-wrap">
                  <Lottie
                    path="/assets/json/identity-confirmed2.json"
                    loop={false}
                    autoplay={true}
                  />
                </div>
                <h2 className="auth-title" style={{ textAlign: "center" }}>{t('twoFactor.success.title')}</h2>
                <p className="auth-subtitle" style={{ textAlign: "center" }}>{t('twoFactor.success.subtitle')}</p>
              </div>
            ) : mode === 'recovery' ? (
              <>
                <h2 className="auth-title">{t('twoFactor.recovery.title') || 'Use a recovery code'}</h2>
                <p className="auth-subtitle">
                  {t('twoFactor.recovery.subtitle') || 'Each code can only be used once.'}
                </p>

                <div className={`tfa-recovery-wrap${shake ? ' shake' : ''}`}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                    {t('twoFactor.recovery.label') || 'Recovery code'}
                  </label>
                  <input
                    type="text"
                    className={`tfa-recovery-input${error ? ' is-err' : ''}`}
                    placeholder={t('twoFactor.recovery.placeholder') || 'XXXX-XXXX-XXXX'}
                    value={recoveryCode}
                    onChange={(e) => {
                      setRecoveryCode(e.target.value.toUpperCase());
                      setError('');
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleVerifyRecovery()}
                    autoComplete="off"
                    autoFocus
                  />
                </div>

                {error && (
                  <div className="login-error" style={{ textAlign: "left", display: "flex", gap: 8, alignItems: "flex-start", marginTop: 12 }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {error}
                  </div>
                )}

                <button
                  className="btn-submit"
                  onClick={handleVerifyRecovery}
                  disabled={loading || !recoveryCode.trim()}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 20 }}
                >
                  {loading ? <><div className="tfa-spinner" />{t('twoFactor.recovery.verifying') || 'Verifying code…'}</> : (t('twoFactor.recovery.verify') || 'Verify recovery code')}
                </button>

                <div className="tfa-footer" style={{ marginTop: 20 }}>
                  <button
                    type="button"
                    onClick={() => { setMode('totp'); setError(''); }}
                    style={{ background: 'none', border: 'none', color: primaryColor, cursor: 'pointer', fontWeight: 500, fontSize: 13 }}
                  >
                    ← {t('twoFactor.backToTotp') || 'Back to authenticator'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="auth-title">{t('twoFactor.title')}</h2>
                <p className="auth-subtitle">
                  {t('twoFactor.subtitle')}
                </p>

                {/* GA badge */}
                <div className="tfa-badge">
                  <div className="tfa-badge-icon-circle">
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        backgroundColor: primaryColor,
                        WebkitMaskImage: 'url("/assets/icons/settingsIcons/secure.svg")',
                        maskImage: 'url("/assets/icons/settingsIcons/secure.svg")',
                        WebkitMaskRepeat: "no-repeat",
                        maskRepeat: "no-repeat",
                        WebkitMaskSize: "contain",
                        maskSize: "contain",
                        WebkitMaskPosition: "center",
                        maskPosition: "center",
                        display: "inline-block",
                      }}
                    />
                  </div>
                  <div className="tfa-badge-text">
                    <b>{t('twoFactor.badge.name') || 'Google Authenticator'}</b>
                    <span>{t('twoFactor.badge.desc') || 'Open the app and find the 6-digit code for this account.'}</span>
                  </div>
                </div>

                {/* 6 digit inputs */}
                <div className={`tfa-digits${shake ? ' shake' : ''}`} onPaste={handlePaste}>
                  {digits.map((d, i) => (
                    <div className="tfa-digit-col" key={i}>
                      <span className={`tfa-bar${d ? ' on' : ''}`} />
                      <input
                        ref={el => inputRefs.current[i] = el}
                        className={`tfa-digit-input${d ? ' has-val' : ''}${error ? ' is-err' : ''}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={d}
                        onChange={e => handleChange(i, e.target.value)}
                        onKeyDown={e => handleKeyDown(i, e)}
                        autoComplete="off"
                      />
                    </div>
                  ))}
                </div>

                <p className="tfa-hint">{t('twoFactor.hint') || 'You can also paste the code directly'}</p>

                {error && (
                  <div className="login-error" style={{ textAlign: "left", display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {error}
                  </div>
                )}

                <button
                  className="btn-submit"
                  onClick={handleVerify}
                  disabled={loading || filled < 6}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                >
                  {loading ? <><div className="tfa-spinner" />{t('twoFactor.verifying')}</> : t('twoFactor.confirm')}
                </button>

                <div className="tfa-footer">
                  {t('twoFactor.lostAccess.prefix') || "Lost access to your authenticator?"}{' '}
                  <button
                    type="button"
                    onClick={() => { setMode('recovery'); setError(''); }}
                    style={{ background: 'none', border: 'none', color: primaryColor, cursor: 'pointer', fontWeight: 500, fontSize: 13, textDecoration: 'underline' }}
                  >
                    {t('twoFactor.lostAccess.linkText') || 'Use a recovery code'}
                  </button>
                </div>
              </>
            )}


          </div>
          <div className="terms-copy">
            {t('signIn.terms.prefix')} <a href="/terms">{t('signIn.terms.tos')}</a>
            {` ${t('signIn.terms.and')} `}
            <a href="/privacy">{t('signIn.terms.privacy')}</a>.
          </div>
        </div>
      </div>

      <style>{`
        .auth-card {
          width: 100%;
          max-width: 800px;
           height: auto;
min-height: 580px;
          display: grid;
          grid-template-columns: 45% 55%;
          border-radius: 18px;
          overflow: hidden;
        }

        .auth-left {
          width: 100%;
          min-width: 0;
          min-height: 0;
          background-image: url('/assets/icons/loginIcon/bannerBg.png');
          background-size: cover;
          background-position: center;
          color: #fff;
          padding: 40px 40px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          overflow-y: auto;
          position: relative;
          box-sizing: border-box;
        }

        .auth-logo-row { display: flex; align-items: center; margin-bottom: 44px; }
        .auth-logo { height: 34px; width: auto; display: block; }

        .auth-eyebrow {
          font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase;
          color: var(--theme-primary, #f95f1f); margin-bottom: 12px;
        }

        .auth-headline { font-size: 28px; font-weight: 700; line-height: 1.2; margin: 0 0 20px 0; color: #fff; }

        .accent-gradient-text {
        font-size: 28px;
        font-weight: 700;
         line-height: 1.2;
          background: linear-gradient(
            90deg,
            #E42E45 0%,
            var(--theme-primary, #F95F1F) 60%,
            #F98B1F 100%
          );
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent; color: transparent;
        }

        .auth-sub-copy { font-size: 13.5px; line-height: 1.6; color: #B4B4B8; max-width: 300px; margin-bottom: 32px; }

        .steps-list { display: flex; flex-direction: column; }
        .step-item { display: flex; gap: 16px; }
        .step-marker { display: flex; flex-direction: column; align-items: center; }
        .step-number {
          width: 26px; height: 26px; border-radius: 50%;
          border: 1px solid var(--theme-primary, #f95f1f); color: var(--theme-primary, #f95f1f);
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 600; flex-shrink: 0;
        }
        .step-connector { width: 1px; flex: 1; min-height: 22px; background: rgba(255,255,255,0.15); margin: 4px 0; }
        .step-copy { padding-bottom: 24px; }
        .step-title { font-size: 14px; font-weight: 600; margin-bottom: 4px; }
        .step-desc { font-size: 12.5px; line-height: 1.5; color: #9CA3AF; }

        .auth-left-footer {
          font-size: 11.5px; line-height: 1.6; color: #8B8B8F;
          border-top: 1px solid rgba(255,255,255,0.1); padding-top: 16px; margin-top: 28px;
        }

        .auth-right {
          width: 100%; min-width: 0; min-height: 0; background: #ffffff; padding: 40px 44px;
          display: flex; flex-direction: column; overflow-y: auto; box-sizing: border-box;
        }

        /* ── Anchored near the top instead of centered, so it doesn't float
           in the middle of a card whose height is set by the taller left panel ── */
        .auth-right-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          padding-top: 36px;
          max-width: 340px;
          margin: 0 auto;
          width: 100%;
        }

        .back-icon-btn {
          width: 36px; height: 36px; border: 1px solid #E5E7EB;
          background: none; border-radius: 10px; display: flex; align-items: center; justify-content: center;
          font-size: 14px; cursor: pointer; margin-bottom: 16px; flex-shrink: 0; transition: background 0.2s ease;
        }
        .back-icon-btn:hover { background: ${colors.primary}14; }

        .auth-title { font-size: 25px; font-weight: 600; color: #111827; margin: 0 0 12px 0; text-align: center; }
        .auth-subtitle { font-size: 14px; color: #6B7280; margin-bottom: 28px; line-height: 1.5; text-align: center; }
        .auth-subtitle strong { color: #374151; font-weight: 600; }

        .login-error {
          background-color: rgba(220, 38, 38, 0.08); color: #DC2626; padding: 12px 16px;
          border-radius: 10px; margin-bottom: 16px; font-weight: 500; font-size: 13px;
        }

        .tfa-badge {
          display: flex; align-items: center; gap: 12px; background: #F9FAFB;
          border: 1px solid #E5E7EB; border-radius: 10px; padding: 12px 16px; margin-bottom: 24px;
        }
        .tfa-badge-icon-circle {
          width: 30px; height: 30px; border-radius: 50%; background: #fff;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }
        .tfa-badge-text b { display: block; font-size: 13px; font-weight: 600; color: #111827; margin-bottom: 4px; }
        .tfa-badge-text span { font-size: 12px; color: #6B7280; line-height: 1.45; }

        .tfa-digits { display: flex; gap: 12px; justify-content: center; margin-bottom: 16px; }
        .tfa-digits.shake { animation: tfashake 0.42s ease; }

        @keyframes tfashake {
          0%,100% { transform: translateX(0); }
          20%      { transform: translateX(-7px); }
          40%      { transform: translateX(7px); }
          60%      { transform: translateX(-4px); }
          80%      { transform: translateX(4px); }
        }

        .tfa-digit-col { display: flex; flex-direction: column; align-items: center; gap: 12px; flex: 1; }

        .tfa-bar { width: 100%; height: 3px; border-radius: 2px; background: #E5E7EB; transition: background 0.2s; }
        .tfa-bar.on { background: var(--theme-primary, #f95f1f); }

        .tfa-digit-input {
          width: 100%; height: 52px;
          background: #ffffff; border: 1.5px solid #E5E7EB; border-radius: 10px;
          box-shadow: 0 1px 2px rgba(17,24,39,0.06), 0 1px 1px rgba(17,24,39,0.04);
          font-size: 22px; font-weight: 700; color: #111827; text-align: center;
          outline: none; caret-color: transparent;
          transition: border-color 0.18s, box-shadow 0.18s, transform 0.15s;
        }
        .tfa-digit-input:focus {
          border-color: var(--theme-primary, #f95f1f);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--theme-primary, #f95f1f) 15%, transparent);
          transform: translateY(-2px);
          background: #fff;
        }
        .tfa-digit-input.has-val { border-color: var(--theme-primary, #f95f1f); background: #fff; color: var(--theme-primary, #f95f1f); }
        .tfa-recovery-wrap { margin-bottom: 12px; }
        .tfa-recovery-wrap.shake { animation: tfashake 0.42s ease; }
        .tfa-recovery-input {
          width: 100%; height: 50px; padding: 0 16px; box-sizing: border-box;
          background: #ffffff; border: 1.5px solid #E5E7EB; border-radius: 10px;
          font-family: monospace, monospace; font-size: 18px; font-weight: 700;
          color: #111827; letter-spacing: 2px; text-align: center; outline: none;
          transition: border-color 0.18s, box-shadow 0.18s;
        }
        .tfa-recovery-input:focus {
          border-color: var(--theme-primary, #f95f1f);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--theme-primary, #f95f1f) 15%, transparent);
        }
        .tfa-recovery-input.is-err { border-color: #ef4444; box-shadow: 0 0 0 3px rgba(239,68,68,0.1); }

        .tfa-hint { font-size: 12px; color: #9CA3AF; text-align: center; margin: 0 0 24px; }

        .btn-submit {
          width: 100%; border: none; background-color: var(--theme-primary, #f95f1f); color: #FFF7ED;
          border-radius: 10px; height: 48px; padding: 0 16px; display: flex; align-items: center; justify-content: center; gap: 8px;
          font-size: 15px; font-weight: 600;
          letter-spacing: 0.3px; cursor: pointer;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }
        .btn-submit:hover:not(:disabled) { transform: translateY(-2px); }
        .btn-submit:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

        .tfa-spinner {
          width: 14px; height: 14px; border: 2px solid rgba(255,255,255,0.35);
          border-top-color: #fff; border-radius: 50%; animation: spin 0.65s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .tfa-footer { text-align: center; font-size: 13px; color: #9CA3AF; margin-top: 20px; }
        .tfa-footer a { color: var(--theme-primary, #f95f1f); text-decoration: none; font-weight: 500; cursor: pointer; }
        .tfa-footer a:hover { text-decoration: underline; }

        .tfa-success { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 100%;  flex: 1;}
        .tfa-lottie-wrap { width: 140px; height: 140px; margin: 0 auto 12px; }

        .terms-copy { text-align: center; font-size: 11.5px; color: #9CA3AF; margin-top: 28px; }
        .terms-copy a { color: #6B7280; text-decoration: underline; }

        @media (max-width: 860px) {
          .auth-card { grid-template-columns: 1fr; }
          .auth-left, .auth-right { width: 100%; }
          .auth-left { padding: 28px 24px; }
          .steps-list { display: none; }
          .auth-right { padding: 32px 24px; }
          .auth-headline, .accent-gradient-text { font-size: 24px; }
        }

        @media (max-width: 480px) {
          /* Bleed the card edge-to-edge and fill the viewport, cancelling the
             page's 16px padding from SessionsLayout instead of floating inset. */
          .auth-card {
            border-radius: 0;
            margin: 0 -16px;
            width: calc(100% + 32px);
            max-width: calc(100% + 32px);
            min-height: 0;
            height: 100vh;
            height: 100dvh;
            grid-template-rows: auto 1fr;
          }

          /* Condense the hero — drop the pitch copy, keep logo + headline. */
          .auth-logo-row { margin-bottom: 20px; }
          .auth-eyebrow, .auth-sub-copy, .auth-left-footer { display: none; }
          .auth-headline, .accent-gradient-text { font-size: 20px; line-height: 1.25; margin: 0; }
          .auth-left { padding: 24px 24px; }

          /* Match the form's left edge to the hero's — the 340px cap was
             getting centered instead of hugging the same padding line. */
          .auth-right { padding: 28px 24px; }
          .auth-right-body { max-width: 100%; margin: 0; padding-top: 20px; }
        }

        @media (max-width: 420px) {
          .auth-left { padding: 16px 20px; }
          .auth-right { padding: 28px 20px; }
          .auth-title { font-size: 22px; }
          .tfa-digits { gap: 8px; }
        }
      `}</style>
    </motion.div>
  );
}